import { useMemo } from 'react';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

/**
 * Resolve the correct system entity ID for the system_volume widget.
 *
 * When the current profile is already a system entity, returns its own ID.
 * When on a site profile, reads the `systems` array from entityDetails and
 * picks the system matching the required owner_type:
 *   - networkRollup=false → owner_type "CORP" (parent system)
 *   - networkRollup=true  → owner_type "OWNR" (network)
 *
 * Returns undefined when no matching system is found — the widget should
 * render an empty state rather than silently substituting another level.
 */
export const useResolveSystemId = (
  entityDetails: Record<string, any> | null | undefined,
  networkRollup: boolean
): string | undefined => {
  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  return useMemo(() => {
    // If we're already on a system profile, use the system's own ID
    if (profileType === 'system') {
      return providerId;
    }

    // On a site (or HCP) profile, look up the system from the systems array
    const systems: Record<string, any>[] = entityDetails?.systems ?? [];
    const targetOwnerType = networkRollup ? 'OWNR' : 'CORP';
    const match = systems.find((s) => s?.owner_type === targetOwnerType);

    return match?.id ?? undefined;
  }, [entityDetails, providerId, profileType, networkRollup]);
};
